import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, UserCog, Users, Plus, Lock, Unlock, Save, Check } from 'lucide-react';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmDelete, confirmActive } from '../../common/confirm';
import { capaInvestigationsService, CapaInvestigation, CapaTeamMember } from '../../../services/capaInvestigationsService';
import { userService, User } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import extractApiError from '../../../utils/apiError';

const userLabel = (u: User) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    if (name && u.login_id) return `${name} (${u.login_id})`;
    return name || u.login_id || `#${u.id}`;
};

interface Props {
    investigation: CapaInvestigation;
    onChanged: () => void;
    onBack: () => void;
    // When embedded (in a Modal), the modal already provides a close (X) so
    // hide the in-surface Back button to reduce whitespace.
    embedded?: boolean;
}

const InvestigationHeader: React.FC<Props> = ({ investigation, onChanged, onBack, embedded }) => {
    const { state } = useAuth();
    const { formatDate } = useEntitySettings();
    const isOwner = !!state.user?.id && state.user.id === investigation.owner_user_id;
    const [team, setTeam] = useState<CapaTeamMember[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [showTransfer, setShowTransfer] = useState(false);
    const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
    const [showAddMember, setShowAddMember] = useState(false);
    const [newMemberId, setNewMemberId] = useState<number | null>(null);
    const [newMemberRole, setNewMemberRole] = useState<CapaTeamMember['expected_action']>('Investigator');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const loadTeam = async () => {
        try {
            setTeam(await capaInvestigationsService.listTeam(investigation.id));
        } catch { /* swallow */ }
    };

    useEffect(() => {
        loadTeam();
        // Use the ungated entity dropdown — staff personas lack USERS_RBAC "view"
        // so getAllUsers() 403s and the picker comes back empty.
        userService.getUsersDropdown()
            .then(rows => setUsers(rows.map(r => ({ id: r.id, first_name: r.name, last_name: '', login_id: '' } as unknown as User))))
            .catch(() => {});
    }, [investigation.id]);

    const handleClose = async () => {
        const ok = await confirmActive({
            title: 'Close this investigation?',
            text: 'The investigation will be archived. The parent CAPA issue remains open until you mark it resolved separately.',
            confirmText: 'Yes, Close',
        });
        if (!ok) return;
        try {
            await capaInvestigationsService.close(investigation.id);
            setToast({ type: 'success', message: 'Investigation closed.' });
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to close') });
        }
    };

    const handleTransfer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newOwnerId) return;
        try {
            setSubmitting(true);
            await capaInvestigationsService.transferOwner(investigation.id, { owner_user_id: newOwnerId });
            setToast({ type: 'success', message: 'Owner transferred successfully' });
            setShowTransfer(false);
            setNewOwnerId(null);
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to transfer') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddMember = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMemberId) return;
        try {
            setSubmitting(true);
            await capaInvestigationsService.addTeamMember(investigation.id, {
                user_id: newMemberId,
                expected_action: newMemberRole,
            });
            setToast({ type: 'success', message: 'Team member added successfully' });
            setShowAddMember(false);
            setNewMemberId(null);
            setNewMemberRole('Investigator');
            loadTeam();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to add member') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleLockToggle = async () => {
        const willLock = !investigation.is_locked;
        const ok = await confirmDelete({
            title: willLock ? 'Lock this investigation?' : 'Unlock this investigation?',
            text: willLock
                ? 'Once locked, no team member can add or edit impacts, causes, team, or remedial actions. Existing remedial-action tasks can still be worked. You (the owner) can unlock anytime.'
                : 'Editing will resume. The team can add/edit impacts, causes, team members, and remedial actions again.',
        });
        if (!ok) return;
        try {
            if (willLock) await capaInvestigationsService.lock(investigation.id);
            else await capaInvestigationsService.unlock(investigation.id);
            setToast({ type: 'success', message: willLock ? 'Locked.' : 'Unlocked.' });
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, willLock ? 'Failed to lock' : 'Failed to unlock') });
        }
    };

    const handleRemoveMember = async (member: CapaTeamMember) => {
        const ok = await confirmDelete({
            title: 'Remove this team member?',
            text: 'They will lose access to make changes on this investigation.',
        });
        if (!ok) return;
        try {
            await capaInvestigationsService.removeTeamMember(investigation.id, member.id);
            setToast({ type: 'delete', message: 'Removed.' });
            loadTeam();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to remove') });
        }
    };

    const isClosed = investigation.status === 'CLOSED';

    const titleContent = (
        <>
            {!embedded && (
                <Button variant="outline" icon={ArrowLeft} onClick={onBack} data-testid="BTN_investigation_header_back">Back</Button>
            )}
            <span className="capa-investigation-code">{investigation.code || investigation.issue_code || `#${investigation.issue_id}`}</span>
            {investigation.code && investigation.issue_code && (
                <span className="text-muted text-xs">{investigation.issue_code}</span>
            )}
            <span className="capa-investigation-title">{investigation.issue_name || 'Untitled'}</span>
            <span className={`status-badge sm ${investigation.status === 'OPEN' ? 'success' : 'neutral'}`}>
                {investigation.status}
            </span>
        </>
    );

    const actionButtons = !isClosed ? (
        <div className="capa-investigation-header-actions">
            {investigation.is_locked && (
                <span className="capa-locked-pill" title={investigation.locked_on ? `Locked on ${formatDate(investigation.locked_on)}` : 'Locked'}>
                    <Lock size={11} /> Locked
                </span>
            )}
            {isOwner && (
                <Button
                    variant="secondary-outline"
                    icon={investigation.is_locked ? Unlock : Lock}
                    onClick={handleLockToggle}
                    data-testid="BTN_investigation_header_lock_toggle"
                >
                    {investigation.is_locked ? 'Unlock' : 'Lock'}
                </Button>
            )}
            <Button variant="secondary-outline" icon={UserCog} onClick={() => setShowTransfer(true)} disabled={!!investigation.is_locked} title={investigation.is_locked ? 'Unlock first' : ''} data-testid="BTN_investigation_header_transfer_owner">Transfer Owner</Button>
            <Button variant="danger" icon={CheckCircle} onClick={handleClose} data-testid="BTN_investigation_header_close">Close Investigation</Button>
        </div>
    ) : undefined;

    return (
        <>
            {embedded ? (
                <Modal.Header onClose={onBack} actions={actionButtons}>
                    {titleContent}
                </Modal.Header>
            ) : (
                <div className="capa-investigation-header">
                    <div className="capa-investigation-header-row">
                        {titleContent}
                        {actionButtons}
                    </div>
                </div>
            )}
            <div className="capa-investigation-meta-row">
                <span className="capa-investigation-owner-line">
                    Owner: <strong>{investigation.owner_name || `user#${investigation.owner_user_id}`}</strong>
                    {investigation.created_by_name && <> · Created by <strong>{investigation.created_by_name}</strong></>}
                    {investigation.due_date && <> · Due {formatDate(investigation.due_date)}</>}
                    {investigation.opened_on && <> · Opened {formatDate(investigation.opened_on)}</>}
                    {isClosed && investigation.closed_on && <> · Closed {formatDate(investigation.closed_on)}</>}
                </span>
                <span className="capa-team-inline">
                    <Users size={13} className="capa-team-inline-icon" />
                    {team.length === 0 && (
                        <span className="capa-team-inline-empty">No team</span>
                    )}
                    {team.map(m => {
                        const name = m.user_name || m.login_id || `user#${m.user_id}`;
                        const initials = name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
                        return (
                            <span
                                key={m.id}
                                className="capa-team-chip"
                                title={`${name}${m.expected_action ? ` — ${m.expected_action}` : ''} (double-click to remove)`}
                                onDoubleClick={() => !isClosed && handleRemoveMember(m)}
                            >
                                {initials}
                            </span>
                        );
                    })}
                    {!isClosed && !investigation.is_locked && (
                        <button
                            type="button"
                            className="capa-team-add"
                            onClick={() => setShowAddMember(true)}
                            title="Add team member"
                            data-testid="BTN_investigation_header_team_add"
                        >
                            <Plus size={11} /> Add
                        </button>
                    )}
                </span>
            </div>

            {/* Transfer Owner Modal */}
            <Modal isOpen={showTransfer} onClose={() => setShowTransfer(false)} size="md">
                <Modal.Header>Transfer Investigation Owner</Modal.Header>
                <Modal.Body>
                    <form id="transfer-owner-form" onSubmit={handleTransfer} className="flex flex-col gap-y-4" data-testid="MOD_investigation_transfer_owner_form">
                        <FormControl.SearchSelect
                            label="New Owner"
                            required
                            options={users.filter(u => u.id !== investigation.owner_user_id).map(u => ({ value: u.id, label: userLabel(u) }))}
                            value={newOwnerId}
                            onChange={v => setNewOwnerId(v ? Number(v) : null)}
                            placeholder="— pick a new owner —"
                            clearable
                            data-testid="SEL_investigation_transfer_new_owner"
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="transfer-owner-form" variant="update" icon={Check} disabled={submitting || !newOwnerId} data-testid="BTN_investigation_transfer_submit">{submitting ? 'Updating...' : 'Update'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Add Team Member Modal */}
            <Modal isOpen={showAddMember} onClose={() => setShowAddMember(false)} size="md">
                <Modal.Header>Create Team Member</Modal.Header>
                <Modal.Body>
                    <form id="add-member-form" onSubmit={handleAddMember} className="grid grid-cols-12 gap-x-6 gap-y-4" data-testid="MOD_investigation_add_member_form">
                        <div className="col-span-6">
                            <FormControl.SearchSelect
                                label="User"
                                required
                                options={users
                                    .filter(u => u.id !== investigation.owner_user_id && !team.some(t => t.user_id === u.id))
                                    .map(u => ({ value: u.id, label: userLabel(u) }))}
                                value={newMemberId}
                                onChange={v => setNewMemberId(v ? Number(v) : null)}
                                clearable
                                data-testid="SEL_investigation_member_user"
                            />
                        </div>
                        <div className="col-span-6">
                            <FormControl.SearchSelect
                                label="Role"
                                options={[
                                    { value: 'Responsible',  label: 'Responsible'  },
                                    { value: 'Accountable',  label: 'Accountable'  },
                                    { value: 'Consulted',    label: 'Consulted'    },
                                    { value: 'Informed',     label: 'Informed'     },
                                    { value: 'Investigator', label: 'Investigator' },
                                ]}
                                value={newMemberRole as any}
                                onChange={v => setNewMemberRole((v as any) || 'Investigator')}
                                data-testid="SEL_investigation_member_role"
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="add-member-form" variant="fill" icon={Save} disabled={submitting || !newMemberId} data-testid="BTN_investigation_member_submit">{submitting ? 'Submitting...' : 'Submit'}</Button>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default InvestigationHeader;
